/**
 * T-009 — Pre-flight atomicity: the pure decision core.
 *
 * The atomicity guarantee: resolve EVERY op's target before mutating anything; if any op fails to
 * locate, abort with zero mutations. That is what turns a failed apply into a clean no-op instead of
 * the partial-edit corruption that blocked the old approach.
 *
 * The Word-touching search lives in `preflightLocate.ts`. This file is the pure, unit-tested brain:
 * given each op and how many `Range.search` matches its value has in the document, it decides
 * whether the whole set resolves and, if so, which match each op must take and the order to apply
 * them in (right-to-left, so earlier edits never invalidate later offsets). It never sees or logs
 * clause text — only counts and indices.
 */

import { OpKind } from './types';
import { LocatedOp } from './locateOps';

export type PreflightFailureReason =
  | 'protected' // op touches a field/bookmark (T-007)
  | 'oversize' // value longer than Word's search limit; needs splitting (T-010)
  | 'unsearchable' // value has characters Range.search rejects (control chars, object anchors)
  | 'occurrence_missing' // fewer matches than the required occurrence index → target not found
  | 'anchor_not_found'; // an insert's anchor text could not be located

export interface PreflightFailure {
  opIndex: number;
  kind: OpKind;
  reason: PreflightFailureReason;
}

/** One resolved op: which search match to act on (null = no search, e.g. insert at paragraph start). */
export interface OpResolution {
  op: LocatedOp;
  /** Original index in the located array (stable identity for the executor). */
  opIndex: number;
  /** Index into the op's search results to act on, or null when no search is needed. */
  matchIndex: number | null;
}

export interface PreflightPlan {
  /** True when every op resolved (nothing skipped). Partial applies have `ok === false`. */
  ok: boolean;
  total: number;
  resolvedCount: number;
  /**
   * The resolvable ops, in right-to-left apply order (by baseline offset, descending). Always
   * populated with whatever resolved — the executor applies these even when some ops were skipped.
   * Each is a pre-resolved, correctly-placed edit, so applying a subset is corruption-safe.
   */
  resolved: OpResolution[];
  /** Ops that could not be placed (field-protected / unsearchable / not found). These are skipped. */
  failures: PreflightFailure[];
}

/**
 * The number of `Range.search` matches found for each op's search value, in the same order as
 * `located`. Use `null` for ops that need no search (an insert anchored at the paragraph start).
 */
export type MatchCounts = Array<number | null>;

/**
 * Decide the pre-flight outcome. Pure: no Word, no I/O.
 *
 * Returns the resolvable ops (right-to-left apply order) AND the ops that could not be placed. The
 * executor applies the resolved ops and skips the failures — a partial apply. `ok` is true only when
 * nothing was skipped. Applying a subset is corruption-safe: every resolved op is a pre-located,
 * correctly-placed edit, and skipping the rest merely leaves those changes unmade (never mislocated).
 *
 * `unsearchable[i]` (optional) marks an op whose search value the caller refused to hand to
 * `Range.search` (control chars / object anchors); such ops are skipped rather than the seam
 * throwing.
 */
export function planPreflight(
  located: LocatedOp[],
  matchCounts: MatchCounts,
  unsearchable: boolean[] = [],
): PreflightPlan {
  if (matchCounts.length !== located.length) {
    throw new Error(
      `planPreflight: matchCounts length ${matchCounts.length} != ops length ${located.length}`,
    );
  }

  const failures: PreflightFailure[] = [];
  const resolutions: OpResolution[] = [];

  located.forEach((op, opIndex) => {
    const count = matchCounts[opIndex];

    // A field deletion resolves by its field range, not a search — always applicable.
    if (op.kind === 'fieldDelete') {
      resolutions.push({ op, opIndex, matchIndex: null });
      return;
    }

    if (op.protected) {
      failures.push({ opIndex, kind: op.kind, reason: 'protected' });
      return;
    }

    if (op.kind === 'delete') {
      if (op.oversize) {
        failures.push({ opIndex, kind: 'delete', reason: 'oversize' });
        return;
      }
      if (unsearchable[opIndex]) {
        failures.push({ opIndex, kind: 'delete', reason: 'unsearchable' });
        return;
      }
      // Need at least occurrenceIndex+1 matches so results[occurrenceIndex] exists.
      if (count === null || count <= op.occurrenceIndex) {
        failures.push({ opIndex, kind: 'delete', reason: 'occurrence_missing' });
        return;
      }
      resolutions.push({ op, opIndex, matchIndex: op.occurrenceIndex });
      return;
    }

    // insert. 'start'/'end' need no search; 'after'/'before' anchor on a search match.
    if (op.anchor.type === 'start' || op.anchor.type === 'end') {
      resolutions.push({ op, opIndex, matchIndex: null });
      return;
    }
    if (unsearchable[opIndex]) {
      failures.push({ opIndex, kind: 'insert', reason: 'unsearchable' });
      return;
    }
    // 'after'/'before' — the anchor text's occurrenceIndex-th match must exist
    if (count === null || count <= op.anchor.occurrenceIndex) {
      failures.push({ opIndex, kind: 'insert', reason: 'anchor_not_found' });
      return;
    }
    resolutions.push({ op, opIndex, matchIndex: op.anchor.occurrenceIndex });
  });

  // Right-to-left apply order: highest baseline offset first, so mutating one op never shifts the
  // document positions of ops not yet applied. Failures are skipped, not fatal.
  const resolved = [...resolutions].sort((a, b) => b.op.offset - a.op.offset);
  return {
    ok: failures.length === 0,
    total: located.length,
    resolvedCount: resolved.length,
    resolved,
    failures,
  };
}
